import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Footer from "../siteComponents/Footer";
import TopBar from "../siteComponents/Topbar";

import { notifyUser } from "../utils/notifyUser";

export default function InvestmentConfirmationPage({ user }) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const campaignId = searchParams.get("campaignId");
    const investmentAmount = searchParams.get("amount");

    const [paymentMethod, setPaymentMethod] = useState("credit_card");
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Fetch campaign details
    useEffect(() => {
        const fetchCampaign = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/view/${campaignId}`,
                );
                // Normalize campaign data - add camelCase versions for snake_case fields
                const normalizedCampaign = {
                    ...res.data,
                    campaignName:
                        res.data.campaignName || res.data.campaign_name,
                };
                setCampaign(normalizedCampaign);
            } catch(error) {
                console.error("Error fetching campaign:", error);
                notifyUser(null, "error", "Failed to load campaign details.", "toast", null, null, null, null, null, false, false, "top-center");
            } finally {
                setLoading(false);
            }
        };

        if(campaignId) {
            fetchCampaign();
        }
    }, [campaignId]);

    const handleInvestNow = async () => {
        if(!investmentAmount || !campaignId || !user || !campaign) {
            notifyUser(null, "error", "Missing required investment information.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Build investor name with fallback
        const investorName =
            `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
            user.email.split("@")[0] ||
            "Investor";

        // Validate all required data
        if(!user.email) {
            notifyUser(null, "error", "User email is required.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(!investorName || investorName === "Investor") {
            console.warn("Using fallback investor name");
        }

        if(!campaign.campaignName) {
            notifyUser(null, "error", "Campaign name is missing.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setSubmitting(true);
        try {
            const requestData = {
                investorEmail: user.email,
                investorName: investorName,
                issuerName: campaign.campaignName,
                amount: investmentAmount,
                paymentMethod:
                    paymentMethod === "credit_card" ? "Credit Card" : "ACH",
                campaignId: campaignId,
                completionDate: new Date().toLocaleDateString(),
            };

            console.log(
                "Sending transaction confirmation request:",
                requestData,
            );

            // Send transaction confirmation email
            const emailResponse = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/emails/send-transaction-confirmation`,
                requestData,
                {
                    withCredentials: true,
                },
            );

            if(emailResponse.status === 200) {
                notifyUser(null, "success", "Investment confirmed! Confirmation email sent.", "toast", null, null, null, null, null, false, false, "top-center");

                // Navigate to investors page after a short delay
                setTimeout(() => {
                    navigate("/investors");
                }, 2000);
            }
        } catch(error) {
            console.error(
                "Error processing investment:",
                error.response?.data || error.message,
            );
            const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.details ||
                "Failed to process investment. Please try again.";
            notifyUser(null, "error", errorMessage, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setSubmitting(false);
        }
    };

    if(loading) {
        return (
            <div>
                <TopBar />
                <div className="min-h-screen flex items-center justify-center">
                    <p className="text-gray-500">Loading...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if(!investmentAmount || !campaignId || !user) {
        return (
            <div>
                <TopBar />
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-red-600 font-semibold text-center p-10">
                        Missing required investment details. Please return to
                        the campaign page.
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <TopBar />
            <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-gray-50 py-8">
                <div className="max-w-lg w-full bg-white shadow-xl rounded-xl p-8">
                    <h1 className="text-2xl font-semibold text-center text-navy mb-2">
                        Confirm Your Investment
                    </h1>
                    <p className="text-center text-gray-600 mb-8">
                        Campaign:{" "}
                        <span className="font-semibold">
                            {campaign?.campaignName}
                        </span>
                    </p>

                    {/* Investment Amount Display */}
                    <div className="bg-blue-50 p-6 rounded-lg mb-8 border border-blue-200">
                        <p className="text-gray-700 text-sm mb-2">
                            Investment Amount
                        </p>
                        <p className="text-4xl font-bold text-navy">
                            $
                            {parseFloat(investmentAmount).toLocaleString(
                                "en-US",
                                {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                },
                            )}
                        </p>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="mb-8">
                        <label className="block text-lg font-medium text-navy mb-4">
                            Select Payment Method
                        </label>
                        <div className="space-y-3">
                            <label
                                className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all"
                                style={{
                                    borderColor:
                                        paymentMethod === "credit_card"
                                            ? "var(--navy)"
                                            : "#e5e7eb",
                                    backgroundColor:
                                        paymentMethod === "credit_card"
                                            ? "#f0f7ff"
                                            : "white",
                                }}
                            >
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="credit_card"
                                    checked={paymentMethod === "credit_card"}
                                    onChange={(e) =>
                                        setPaymentMethod(e.target.value)
                                    }
                                    className="w-4 h-4 text-navy"
                                />
                                <span className="ml-3">
                                    <span className="text-lg font-medium text-navy">
                                        Credit Card
                                    </span>
                                    <p className="text-sm text-gray-500">
                                        Visa, Mastercard, American Express
                                    </p>
                                </span>
                            </label>

                            <label
                                className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all"
                                style={{
                                    borderColor:
                                        paymentMethod === "ach"
                                            ? "var(--navy)"
                                            : "#e5e7eb",
                                    backgroundColor:
                                        paymentMethod === "ach"
                                            ? "#f0f7ff"
                                            : "white",
                                }}
                            >
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="ach"
                                    checked={paymentMethod === "ach"}
                                    onChange={(e) =>
                                        setPaymentMethod(e.target.value)
                                    }
                                    className="w-4 h-4 text-navy"
                                />
                                <span className="ml-3">
                                    <span className="text-lg font-medium text-navy">
                                        ACH Transfer
                                    </span>
                                    <p className="text-sm text-gray-500">
                                        Direct bank transfer
                                    </p>
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Investment Summary */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-8 border border-gray-200">
                        <h3 className="font-semibold text-navy mb-3">
                            Investment Summary
                        </h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">
                                    Investment Amount:
                                </span>
                                <span className="font-medium text-navy">
                                    $
                                    {parseFloat(
                                        investmentAmount,
                                    ).toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">
                                    Payment Method:
                                </span>
                                <span className="font-medium text-navy">
                                    {paymentMethod === "credit_card"
                                        ? "Credit Card"
                                        : "ACH Transfer"}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">
                                    Investor Name:
                                </span>
                                <span className="font-medium text-navy">
                                    {user.first_name} {user.last_name}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Invest Now Button */}
                    <button
                        onClick={handleInvestNow}
                        disabled={submitting}
                        className={`w-full py-3 rounded-lg font-semibold text-white text-lg transition-all ${
                            submitting
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-navy hover:bg-navy-2"
                        }`}
                    >
                        {submitting ? "Processing..." : "Invest Now"}
                    </button>

                    {/* Terms and Conditions */}
                    <p className="text-center text-xs text-gray-500 mt-4">
                        By clicking &quot;Invest Now&quot;, you agree to our{" "}
                        <a href="/terms" className="text-navy underline">
                            Terms and Conditions
                        </a>{" "}
                        and{" "}
                        <a
                            href="/docs/privacy-policy"
                            className="text-navy underline"
                        >
                            Privacy Policy
                        </a>
                    </p>
                </div>
            </div>
            <Footer />
        </>
    );
}
InvestmentConfirmationPage.propTypes = {
    user: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string,
    }),
};
